/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ContentBox,
  QuestionBox,
  TextInput,
  PropertyLabel,
  PropertyValue,
  Main,
  SectionLeft,
  SectionRight,
  PreContentBoxRight,
  ActionLink,
  ContinueButton,
} from '../generateQuestionTemplate/generateQuestion.style';
import { ErrorMessageComponent } from '../../../components/uielements/error';

import { CheckCircle, SkipForward, X, MessageCircle, HelpCircle, LoaderCircle } from 'lucide-react';
import {
  ActionButton,
  ConversationAnswer,
  ConversationItem,
  ConversationQuestion,
  InputContainer,
  InputLabel,
  InputWrapper,
  SendButton,
  SendButtonContainer,
} from './preLodgement.style';
import { getChatBotRespond } from '../../../services/chatBot';
import ConfirmationModal from '../../../components/uielements/modal/confirmationModal';
import { saveQueryResponse } from '../../../services/application';
import { getItem, setItem } from '../../../helper/localStorageHelper';
import { IntlMessages } from '../../../helper/langHelper';
import { useIntl } from 'react-intl';
import { getZoneDetails } from '../../../services/getSummary';
import { getPrelodgementQuestions, saveApplication } from '../../../services/questionService';
import { getSummaryGenerator } from '../../../services/getSummary';
import MapWithOverlays from '../mapView/mapWithOverlays';
import { formatBotMessage } from '../../../helper/aiRespondFormatter';
import { getCacheOverlaysScreenshots } from '../../../helper/cacheHelper';

const PHASES = {
  PRELODGEMENT_QUESTIONS: 'PRELODGEMENT_QUESTIONS',
  CHATBOT: 'CHATBOT',
  COMPLETED: 'COMPLETED',
};

const PreLodgementQuestionTemplate = () => {
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEndQuestion, setIsEndQuestion] = useState(false);
  const [isProcedureCompleted, setIsProcedureCompleted] = useState(false);
  const [conversations, setConversations] = useState([]);

  // Prelodgement questions flow
  const [currentPhase, setCurrentPhase] = useState(PHASES.PRELODGEMENT_QUESTIONS);
  const [prelodgementQuestions, setPrelodgementQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [prelodgementAnswers, setPrelodgementAnswers] = useState([]);
  const [prelodgementConverstation, setPrelodgementConversation] = useState([]);

  // Modal states
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [zoneDetails, setZoneDetails] = useState({});
  const [zoneError, setZoneError] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Navigation hooks
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const flow = searchParams.get('flow');
  const cacheOverlayScreenshots = getCacheOverlaysScreenshots();

  const intl = useIntl();

  const selectedProcedure = location.state?.selectedProcedure || '';
  const propertyAddress = location.state?.searchAddress?.address || '';
  const overlays = location.state?.overlays || '';
  const fullName = location.state?.fullName || '';
  const description = location.state?.inputValue || '';
  const searchAddress = location.state?.searchAddress || '';
  const email = location.state?.email || '';
  const phone = location.state?.phone || '';
  const screenshotId = getItem('screenshotId');

  useEffect(() => {
    if (flow === 'prelodgement') {
      fetchZoneDetails();
      fetchPrelodgementQuestions();
    }
  }, []);

  // Fetch prelodgement questions
  const fetchPrelodgementQuestions = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getPrelodgementQuestions({ procedureId: selectedProcedure?._id });

      if (response?.questionArray && response.questionArray.length > 0) {
        setPrelodgementQuestions(response.questionArray);
        setCurrentPhase(PHASES.PRELODGEMENT_QUESTIONS);
      } else {
        // No questions, move directly to chatbot phase
        setCurrentPhase(PHASES.CHATBOT);
      }
    } catch (err) {
      console.error('Error fetching prelodgement questions:', err);
      setError('Failed to retrieve prelodgement questions.');
      // Move to chatbot phase on error
      setCurrentPhase(PHASES.CHATBOT);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch zone details
  const fetchZoneDetails = async () => {
    setLoading(true);
    setZoneError('');

    try {
      const zoneData = await getZoneDetails({ propertyAddress });
      setZoneDetails(zoneData);
    } catch (err) {
      setZoneError("Oops! We couldn't retrieve the zone details.");
      setError('Failed to retrieve zone details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Prepare application payload
  const getApplicationPayload = () => {
    return {
      address: propertyAddress,
      description: description || 'description',
      fullName: fullName,
      procedureId: selectedProcedure?._id,
      overlayArray: overlays?.map(({ overlayName }) => ({ overlay: overlayName })),
      propertyDetails: zoneDetails,
      phoneNumber: phone,
      email: email,
      screenshotId,
      prelodgementQuestionArray: prelodgementConverstation,
      recordId: cacheOverlayScreenshots?.find((item) => item?.name === true)?.screenshot,
    };
  };

  // Handle submitting prelodgement question answer
  const handlePrelodgementSubmit = useCallback(async () => {
    if (!answer.trim()) {
      setError('Please provide an answer.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const currentQuestion = prelodgementQuestions[currentQuestionIndex];

      const payload = {
        question: currentQuestion,
        responseData: answer,
      };
      const data = await getSummaryGenerator(payload);
      const summaryText = data?.output || 'Unable to generate summary.';

      // Store in state
      const answerData = {
        question: currentQuestion,
        answer: summaryText,
        timestamp: new Date().toISOString(),
      };
      const prelodgementQnsAns = {
        question: currentQuestion,
        answer: summaryText,
      };

      // Add new items at the beginning to keep most recent on top
      setPrelodgementAnswers((prev) => [answerData, ...prev]);
      setPrelodgementConversation((prev) => [prelodgementQnsAns, ...prev]);

      // Move to next question or finish
      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex < prelodgementQuestions.length) {
        setCurrentQuestionIndex(nextIndex);
        setAnswer('');
      } else {
        // All prelodgement questions done, move to chatbot phase
        setCurrentPhase(PHASES.CHATBOT);
        setAnswer('');
      }
    } catch (err) {
      setError('Failed to save answer. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [answer, currentQuestionIndex, prelodgementQuestions]);

  // Handle submitting chatbot question
  const handleChatbotSubmit = useCallback(async () => {
    if (!answer.trim()) return;

    setLoading(true);
    setError('');

    try {
      // Call chatbot API
      const response = await getChatBotRespond({ question: answer });

      // Create new conversation item
      const newConversation = {
        question: answer,
        answer: response?.output || 'No response received',
      };

      // Update conversations and clear input - add at beginning for most recent on top
      setConversations((prev) => [newConversation, ...prev]);
      setAnswer('');
    } catch (err) {
      setError('Failed to get response. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [answer]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      if (currentPhase === PHASES.PRELODGEMENT_QUESTIONS) {
        handlePrelodgementSubmit();
      } else if (currentPhase === PHASES.CHATBOT) {
        handleChatbotSubmit();
      }
    }
  };

  const handleSkip = () => {
    setShowSkipModal(true);
  };

  const confirmSkip = async () => {
    if (processingAction) return;

    setProcessingAction(true);
    setError('');

    try {
      if (flow === 'prelodgement') {
        const applicationPayload = getApplicationPayload();
        const applicationResponse = await saveApplication(applicationPayload);
        const newApplicationId = applicationResponse?.applicationId;

        if (!newApplicationId) {
          throw new Error('Failed to create application');
        }

        setItem('applicationId', newApplicationId);
      }

      const newApplicationId = getItem('applicationId');

      await saveQueryResponse({
        applicationId: newApplicationId,
        queryArray: [
          {
            question: 'SKIP',
            answer: 'SKIP',
          },
        ],
      });

      navigate('/preLodgementReport', {
        state: { selectedProcedure, searchAddress },
      });
    } catch (error) {
      setError('Failed to skip procedure. Please try again later.');
    } finally {
      setProcessingAction(false);
      setShowSkipModal(false);
    }
  };

  const handleTerminate = () => {
    setShowTerminateModal(true);
  };

  const confirmTerminate = async () => {
    if (processingAction) return;

    setProcessingAction(true);
    setError('');

    try {
      if (flow === 'prelodgement') {
        const applicationPayload = getApplicationPayload();
        const applicationResponse = await saveApplication(applicationPayload);
        const newApplicationId = applicationResponse?.applicationId;

        if (!newApplicationId) {
          throw new Error('Failed to create application');
        }

        setItem('applicationId', newApplicationId);
      }

      const newApplicationId = getItem('applicationId');

      await saveQueryResponse({
        applicationId: newApplicationId,
        queryArray: conversations,
      });

      setIsProcedureCompleted(true);
    } catch (error) {
      setError('Failed to complete termination. Please try again later.');
    } finally {
      setProcessingAction(false);
      setShowTerminateModal(false);
    }
  };

  const handleGenerateReport = () => {
    setIsEndQuestion(false);
    navigate('/preLodgementReport', {
      state: { selectedProcedure, searchAddress },
    });
  };

  const calculateProgress = () => {
    if (currentPhase === PHASES.PRELODGEMENT_QUESTIONS && prelodgementQuestions.length > 0) {
      return Math.round(((currentQuestionIndex + 1) / prelodgementQuestions.length) * 100);
    }
    return 0;
  };

  return (
    <Main role="main">
      <ConfirmationModal
        isModalOpen={showSkipModal}
        onClose={() => setShowSkipModal(false)}
        onConfirm={confirmSkip}
        title={<IntlMessages id="modal.skip.title" />}
        message={
          processingAction ? (
            <IntlMessages id="modal.skip.message.loading" />
          ) : (
            <IntlMessages id="modal.skip.message.confirm" />
          )
        }
        confirmText={<IntlMessages id="modal.skip.confirmText" />}
        cancelText={<IntlMessages id="modal.skip.cancelText" />}
      />

      <ConfirmationModal
        isModalOpen={showTerminateModal}
        onClose={() => setShowTerminateModal(false)}
        onConfirm={confirmTerminate}
        title={<IntlMessages id="modal.end.title" />}
        message={<IntlMessages id="modal.end.message" />}
        confirmText={<IntlMessages id="modal.end.confirmText" />}
        cancelText={<IntlMessages id="modal.skip.cancelText" />}
      />

      <SectionLeft
        className="hide-scroll-bar"
        aria-label="Question Section"
        isEndQuestion={isEndQuestion}
      >
        {error ? (
          <ContentBox>
            <ErrorMessageComponent message={error} role="alert" aria-live="assertive" />
          </ContentBox>
        ) : (
          <>
            {isProcedureCompleted ? (
              <ContentBox>
                <QuestionBox role="heading" aria-level="1">
                  <PropertyLabel as="h2" style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={24} style={{ color: '#3DB654', marginRight: '8px' }} />
                    <IntlMessages id="prelodgement.completed.title" />
                  </PropertyLabel>
                </QuestionBox>
                <p style={{ fontSize: '14px', lineHeight: '22px' }}>
                  <IntlMessages id="prelodgement.completed.message" />{' '}
                  <ActionLink onClick={handleGenerateReport}>
                    <IntlMessages id="button.prelodgement.report" />
                  </ActionLink>
                </p>
              </ContentBox>
            ) : currentPhase === PHASES.PRELODGEMENT_QUESTIONS ? (
              <ContentBox
                role="region"
                aria-label="Prelodgement Questions"
                className="hide-scroll-bar"
              >
                {/* Progress indicator */}
                {prelodgementQuestions.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <PropertyLabel style={{ fontSize: '14px', marginBottom: '8px' }}>
                      Question {currentQuestionIndex + 1} of {prelodgementQuestions.length}
                    </PropertyLabel>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${calculateProgress()}%`,
                          height: '100%',
                          backgroundColor: '#3b82f6',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </div>
                )}

                <InputWrapper>
                  <QuestionBox role="heading" aria-level="1">
                    {loading && <LoaderCircle size={40} className="custom-spinner" />}{' '}
                    {prelodgementQuestions[currentQuestionIndex] || 'Loading question...'}
                  </QuestionBox>
                  <div role="form">
                    <TextInput
                      id="prelodgement-answer-input"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'question.placeholder' })}
                      aria-required="true"
                      onKeyDown={handleKeyDown}
                      disabled={loading}
                    />
                  </div>
                </InputWrapper>

                <ContinueButton
                  onClick={handlePrelodgementSubmit}
                  disabled={!answer.trim() || loading}
                  aria-disabled={!answer.trim() || loading}
                >
                  {currentQuestionIndex < prelodgementQuestions.length - 1 ? (
                    <IntlMessages id="button.next" defaultMessage="Next" />
                  ) : (
                    'Complete'
                  )}
                </ContinueButton>
              </ContentBox>
            ) : (
              <ContentBox role="region" aria-label="Question Form" className="hide-scroll-bar">
                <InputWrapper>
                  <InputLabel htmlFor="answer-input">
                    {loading || processingAction ? (
                      <LoaderCircle size={40} className="custom-spinner" />
                    ) : (
                      <HelpCircle size={28} />
                    )}{' '}
                    <IntlMessages id="question.label" />
                  </InputLabel>
                  <div role="form">
                    <TextInput
                      id="answer-input"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'question.placeholder' })}
                      aria-labelledby="answer-label"
                      aria-required="true"
                      onKeyDown={handleKeyDown}
                      disabled={loading || processingAction}
                    />
                  </div>
                </InputWrapper>

                <InputContainer
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <SendButtonContainer
                    style={{
                      display: 'flex',
                      gap: '10px',
                    }}
                  >
                    {conversations?.length === 0 && (
                      <ActionButton
                        variant="primary"
                        onClick={handleSkip}
                        disabled={loading || processingAction}
                      >
                        <SkipForward size={20} /> <IntlMessages id="button.skip" />
                      </ActionButton>
                    )}
                    {conversations?.length > 0 && (
                      <ActionButton
                        variant="primary"
                        onClick={handleTerminate}
                        disabled={loading || processingAction}
                      >
                        <X size={20} /> <IntlMessages id="button.end.session" />
                      </ActionButton>
                    )}
                  </SendButtonContainer>

                  <SendButton
                    onClick={handleChatbotSubmit}
                    disabled={!answer || loading || processingAction}
                    aria-disabled={!answer || loading || processingAction}
                  >
                    ➤
                  </SendButton>
                </InputContainer>
              </ContentBox>
            )}
          </>
        )}

        <PreContentBoxRight>
          <section aria-label="Conversation Queries">
            <PropertyLabel
              as="h2"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}
            >
              <MessageCircle size={24} /> <IntlMessages id="conversation.section.title" />
            </PropertyLabel>

            {/* Show chatbot conversations */}
            {conversations.length > 0 && (
              <>
                {prelodgementAnswers.length > 0 && (
                  <PropertyLabel
                    style={{ fontSize: '14px', marginTop: '16px', marginBottom: '8px' }}
                  >
                    Follow-up Questions
                  </PropertyLabel>
                )}
                {conversations.map((conv, index) => (
                  <ConversationItem key={`chat-${index}`}>
                    <ConversationQuestion>{conv.question}</ConversationQuestion>
                    <ConversationAnswer>{formatBotMessage(conv.answer)}</ConversationAnswer>
                  </ConversationItem>
                ))}
              </>
            )}

            {/* Show prelodgement answers */}
            {prelodgementAnswers.length > 0 && (
              <>
                <PropertyLabel style={{ fontSize: '14px', marginTop: '16px', marginBottom: '8px' }}>
                  Initial Questions
                </PropertyLabel>
                {prelodgementAnswers.map((item, index) => (
                  <ConversationItem key={`prelodge-${index}`}>
                    <ConversationQuestion>{item.question}</ConversationQuestion>
                    <ConversationAnswer>{item.answer}</ConversationAnswer>
                  </ConversationItem>
                ))}
              </>
            )}

            {prelodgementAnswers.length === 0 && conversations.length === 0 && (
              <PropertyValue>
                <IntlMessages id="conversation.empty.state" />
              </PropertyValue>
            )}
          </section>
        </PreContentBoxRight>
      </SectionLeft>

      <SectionRight>
        <MapWithOverlays searchAddress={propertyAddress} showDropdown={true} />
      </SectionRight>
    </Main>
  );
};

export default PreLodgementQuestionTemplate;
